import { useTranslation } from 'react-i18next';
import { formatCallDateTime, formatUniqueContactId } from './takataFormat';
import { useTakataCallLog } from './useTakataCalls';

interface TakataCallLogProps {
  campaignEntryId: number;
  wave: string | null;
}

/**
 * The expandable detail row: every attempt made for one campaign entry, where
 * the grid row above shows only the final one.
 *
 * The dealer identity columns (PhysOut, Actor Code, Company Name, Brand, Town,
 * Address, Wave, Quarter) are deliberately not repeated — they are already on
 * the parent row. What is left identifies the individual attempt.
 */
export function TakataCallLog({ campaignEntryId, wave }: TakataCallLogProps) {
  const { t } = useTranslation('mystery/dealerlist');
  const { data, isLoading, isError } = useTakataCallLog(campaignEntryId, wave);

  if (isLoading) {
    return <div className="tk-log-empty">{t('Grid.Processing', 'Loading…')}</div>;
  }

  if (isError) {
    return <div className="tk-log-empty">{t('TakataCalls.Log.Error', 'Could not load call log')}</div>;
  }

  if (!data || data.length === 0) {
    return <div className="tk-log-empty">{t('TakataCalls.Log.Empty', 'No call history')}</div>;
  }

  return (
    <div className="tk-log-wrap">
      <table className="tk-log-table">
        <thead>
          <tr>
            <th>{t('TakataCalls.Col.CalledPhoneNumber', 'Called Phone Number')}</th>
            <th>{t('TakataCalls.Col.CallDate', 'Call Date (CET)')}</th>
            <th>{t('TakataCalls.Col.OutcomeDetails', 'Outcome Details')}</th>
            <th>{t('TakataCalls.Col.UniqueContactId', 'Unique Contact Id')}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.callId}>
              <td>{row.calledPhoneNumber}</td>
              <td>{formatCallDateTime(row.outcomeDate)}</td>
              <td>{row.outcomeDetails}</td>
              <td>{formatUniqueContactId(row.campaignEntryId, row.uniqueContactId)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
